import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Grid,
  IconButton,
  Paper,
  Typography,
  withMobileDialog,
} from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import classNames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';

import { CreditCard, HELP_URL, Payline, PaymentProvider, Primaccess, helpers } from '@aps-management/primapp-common';

import GetCreditCard from '_components/GetCreditCard';
import { Screen } from '_components/core';
import { Alert, CreditCardPreview, PaylineWidgetFrame } from '_components/elements';
import apolloClient from '_utils/apolloClient';
import i18n from '_utils/i18n';

/* */
import { PAYLINE_FLOW_TYPES, setupPaylineListeners } from '../../services/payline';

/* */
const styles = (theme) => ({
  panel: {
    display: 'inherit',
  },
  alert: {
    marginTop: theme.spacing.unit * 2,
  },
  largeButton: {
    width: 'calc(100% / 3)',
    marginLeft: 'calc(100% / 3)',
    marginRight: 'calc(100% / 3)',
  },
  dialogButton: {
    width: 150,
  },
  dialog: {
    minWidth: 300,
    backgroundColor: theme.palette.background.default,
  },
  dialogTitle: {
    borderBottom: '1px solid #D9D9D9',
  },
  dialogContent: {
    padding: 15,
  },
  dialogActions: {
    borderTop: '1px solid #D9D9D9',
    margin: 0,
    padding: 25,
  },
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  textContainer: {
    margin: '50px 25px',
  },
  bold: {
    fontWeight: 800,
  },
  product: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    padding: theme.spacing.unit * 1.5,
    marginBottom: theme.spacing.unit * 2,
  },
  selectedProduct: {
    boxShadow: '0px 1px 5px 1px rgba(0, 109, 183, 1)',
  },
});

/* */
class AutoRefill extends React.Component {
  /* */
  constructor(props) {
    super(props);

    const { golf, account } = this.props;

    this.currency = golf.currency;
    this.authorizationAmount = 100;

    this.state = {
      error: null,
      success: null,
      loading: true,
      imprint: null,
      expanded: false,
      products: [],
      refillStatus: null,
      buttonLoading: false,
      optOutModalOpened: false,
      chooseProductModalOpened: false,
      selectCreditCardModalOpened: false,
      // Payline
      paylineToken: null,
      paylineLoading: false,
    };

    this.paylineWidgetData = null;
    this.isPaylinePaymentHandled = false;
    this.removePaylineListeners = null;
  }

  /* */
  componentDidMount() {
    this.load();
  }

  /*
   * `setupPaylineListeners` pose ses écouteurs sur `window`, pas sur le composant :
   * sans cette libération, ils survivent au démontage de l'écran. Un aller-retour
   * laisse alors deux instances à l'écoute du même widget, et le succès du paiement
   * déclenche DEUX fois la chaîne de confirmation - chacune avec son propre
   * garde-fou d'instance, qui ne voit donc rien. Cf. incident Golf aux Daims du
   * 25/07/2026.
   */
  componentWillUnmount() {
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }
  }

  /* */
  load = () => {
    const { account, golf } = this.props;

    this.setState({
      loading: true,
      imprint: null,
    });

    Primaccess.api
      .getRefillStatus(apolloClient, {
        accountId: account.id,
        golfId: golf.id,
      })
      .then(({ refillStatus }) => {
        Primaccess.api
          .getProducts(apolloClient, {
            accountId: account.id,
            golfId: golf.id,
          })
          .then((products) => {
            products.sort(({ price: a }, { price: b }) => a - b);
            this.setState({
              loading: false,
              refillStatus,
              products,
              product: (refillStatus && JSON.parse(refillStatus.product)) || products[0],
            });
          });
      });

    this.loadCreditCards();
  };

  /**
   * Charge l'empreinte utilisable pour le rechargement automatique.
   *
   * Seule une carte disposant d'un wallet Payline réel peut être débitée en
   * server-to-server, et c'est exactement ce que fait le rechargement automatique
   * (`master rest/public/primaccess.js`, `captureSubscriber`). Une carte sans
   * `walletId` faisait échouer CHAQUE rechargement, en silence, alors que l'écran
   * affichait un rechargement actif : le golfeur ne le découvrait que bloqué
   * devant la borne.
   *
   * Même garde que le paiement 1 clic (`Primaccess/Cart/Payment.js`) : la carte
   * n'est pas proposée, l'utilisateur en saisit une nouvelle et le widget recrée
   * un wallet valide.
   *
   * Cette méthode est aussi le point de passage APRÈS un enregistrement de carte :
   * l'empreinte vient toujours du serveur, jamais des valeurs du formulaire, qui
   * ne portent pas de `walletId`.
   */
  loadCreditCards = () => {
    const { account } = this.props;

    return CreditCard.api
      .getCreditCards(apolloClient, { accountId: account.id })
      .then(({ creditCards }) => {
        const newState = {
          imprint: null,
          expanded: true,
        };

        const [usable] = (creditCards || []).filter((c) => c && c.walletId);

        if (usable) {
          const status = CreditCard.functions.getStatus(usable.dueDate);
          newState.imprint = {
            ...status,
            ...usable,
          };
          newState.expanded = status.isExpired;
        }

        this.setState(newState);
      })
      .catch(() => {});
  };

  /* */
  handleSubmit = (values, { setSubmitting, resetForm }) => {
    const { id3D = null } = values;
    const { account } = this.props;

    const uuid = helpers.uuid.createUuid();

    const creditCard = {
      ...values,
      name: '3DS web',
      number: values.number.replace(/\s/g, ''),
      dueDate: values.dueDate.replace(/\D/g, ''),
    };

    this.setState({ error: null, success: null });

    return Payline.api
      .createSubscriber(apolloClient, {
        id3D,
        uuid,
        reference: account.id,
        amount: this.authorizationAmount,
        creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
      })
      .catch((err) => {
        throw Object.assign(err, { handled: true });
      })
      .then((res) =>
        CreditCard.api.setCreditCard(apolloClient, {
          id: uuid,
          token: res.token,
          number: CreditCard.functions.anonymizeNumber(creditCard.number),
          ...helpers.object.filterByKeys(creditCard, ['name', 'dueDate', 'type', 'brand', 'country']),
        }),
      )
      .catch((err) => {
        if (!err.handled) {
          throw Object.assign(err, {
            handled: true,
            message: 'Impossible d’enregistrer la carte. Veuillez réessayer ultérieurement.',
          });
        }
        throw err;
      })
      .then(() => {
        // L'empreinte est relue depuis le serveur : les valeurs du formulaire ne
        // portent pas de `walletId`, et une empreinte sans wallet rendrait le
        // rechargement automatique inopérant (cf. `loadCreditCards`).
        this.setState({ success: 'Votre carte est enregistrée avec succès.' });
        resetForm();
        return this.loadCreditCards();
      })
      .catch((err) => this.setState({ error: err.message }))
      .finally(() => setSubmitting(false));
  };

  /* */
  handlePaylineCardRegistration = () => {
    const { account, golf } = this.props;

    this.setState({
      error: null,
      success: null,
      paylineLoading: true,
    });

    const baseUrl = window.location.origin;

    return Payline.api
      .getToken(apolloClient, {
        amount: this.authorizationAmount,
        currency: this.currency,
        reference: `AR-${helpers.uuid.createUuid().split('-')[0]}`,
        returnUrl: `${baseUrl}/primaccess`,
        cancelUrl: `${baseUrl}/primaccess/autorefill`,
        callbackUrl: `${baseUrl}/payline/callback`,
        action: '100',
        customer: {
          firstName: account.firstname,
          lastName: account.lastname,
          email: account.email,
          phone: account.phone,
        },
      })
      .then((res) => {
        if (!res?.token) {
          throw new Error('Token de paiement introuvable.');
        }

        this.paylineWidgetData = { token: res.token };
        this.isPaylinePaymentHandled = false;

        this.setState({
          paylineToken: res.token,
          paylineLoading: false,
        });

        if (this.removePaylineListeners) {
          this.removePaylineListeners();
        }

        this.removePaylineListeners = setupPaylineListeners(PAYLINE_FLOW_TYPES.CHECKOUT, {
          onSuccess: (eventData) => this.handlePaylineSuccess(eventData),
          onFailure: (eventData) =>
            this.handlePaylineError(eventData, "L'enregistrement de la carte a échoué. Veuillez réessayer."),
          onCancel: (eventData) =>
            this.handlePaylineError(eventData, "L'enregistrement de la carte a été annulé."),
        });
      })
      .catch((err) => {
        this.setState({
          error: err.message,
          paylineLoading: false,
        });
      });
  };

  /* */
  handlePaylineSuccess = (eventData = {}) => {
    if (this.isPaylinePaymentHandled || !this.paylineWidgetData) {
      return;
    }

    this.isPaylinePaymentHandled = true;

    const token = eventData?.token || this.paylineWidgetData.token;

    // Drapeau de capture : sépare l'échec RÉCUPÉRABLE (avant tout mouvement d'argent)
    // de l'échec AVAL (capture acceptée, un rejeu débiterait une seconde fois).
    // Motif repris de primagolf-hq/screens/Customer/ConfirmRecurPayment.
    let captureSucceeded = false;

    this.setState({ paylineToken: null });
    // `paylineWidgetData` n'est PAS remis à null ici : il l'était avant l'appel
    // réseau, ce qui rendait mort-né le rejeu que le `catch` prétendait autoriser.
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }

    this.setState({
      error: null,
      status: 'Enregistrement de la carte...',
    });

    const uuid = helpers.uuid.createUuid();

    return Payline.api
      .captureWithToken(apolloClient, {
        token,
        amount: this.authorizationAmount,
        uuid,
        reference: `AR-${uuid.split('-')[0]}`,
        customer: {
          firstName: this.props.account.firstname,
          lastName: this.props.account.lastname,
          email: this.props.account.email,
        },
      })
      .then((result) => {
        if (!result) {
          throw new Error("Impossible de vérifier l'enregistrement de la carte.");
        }

        // À partir d'ici l'autorisation est passée : plus aucun rejeu acceptable.
        captureSucceeded = true;

        return CreditCard.api
          .setCreditCard(apolloClient, {
            id: uuid,
            token: result.token || token,
            number: result.maskedCardNumber,
            name: 'Payline',
            dueDate: result.expiryDate
              ? result.expiryDate
              : result.expirationDate
                ? result.expirationDate
                : '0000',
            brand: result.cardBrand,
            country: result.cardCountry,
            walletId: result.walletId,
            paymentRecordId: result.transactionId,
          })
          .then(() => {
            // Relecture serveur plutôt que reconstruction locale : la création du
            // wallet Payline est best-effort côté master (`captureWithToken` renvoie
            // le paiement même si `createWallet` a échoué), donc `result.walletId`
            // peut être absent. `loadCreditCards` refuse alors l'empreinte au lieu
            // d'activer un rechargement automatique qui échouerait à chaque passage.
            this.setState({ success: 'Votre carte est enregistrée avec succès.' });
            this.paylineWidgetData = null;
            return this.loadCreditCards();
          });
      })
      .catch((err) => {
        // Ne relâcher le verrou que si RIEN n'a été autorisé (cf. captureSucceeded).
        if (!captureSucceeded) {
          this.isPaylinePaymentHandled = false;
        } else {
          this.paylineWidgetData = null;
        }
        this.setState({ error: err.message });
      });
  };

  /* */
  handlePaylineError = (eventData = {}, defaultMessage = 'Une erreur est survenue.') => {
    this.setState({ paylineToken: null });
    this.paylineWidgetData = null;
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }

    this.setState({
      paylineLoading: false,
      error: eventData?.message || defaultMessage,
    });

    this.isPaylinePaymentHandled = false;
  };

  /* */
  enableRefill = () => {
    const { account, golf } = this.props;
    const { product } = this.state;

    this.setState({
      buttonLoading: true,
    });
    Primaccess.api
      .setRefillStatus(apolloClient, {
        accountId: account.id,
        golfId: golf.id,
        status: true,
        product: JSON.stringify(product),
      })
      .then((setRefillStatus) => {
        if (setRefillStatus) {
          this.setState({
            buttonLoading: false,
            chooseProductModalOpened: false,
            selectCreditCardModalOpened: false,
            refillStatus: setRefillStatus,
          });
        }
      });
  };

  /* */
  disableRefill = () => {
    const { account, golf } = this.props;

    this.setState({
      buttonLoading: true,
    });
    Primaccess.api
      .setRefillStatus(apolloClient, {
        accountId: account.id,
        golfId: golf.id,
        status: false,
      })
      .then((setRefillStatus) => {
        if (setRefillStatus) {
          this.setState({
            buttonLoading: false,
            optOutModalOpened: false,
            refillStatus: setRefillStatus,
          });
        }
      });
  };

  /* */
  toggleModal = (name) => {
    this.setState({ [`${name}ModalOpened`]: !this.state[`${name}ModalOpened`] });
  };

  /* */
  handleExpand = (event, expanded) => this.setState({ expanded });

  /* */
  renderForm(alreadyExisting) {
    const { classes } = this.props;
    const { expanded } = this.state;

    const form = (
      <GetCreditCard
        currency={this.currency}
        onSubmit={this.handleSubmit}
        authorizationAmount={this.authorizationAmount}
      />
    );

    return (
      <ExpansionPanel expanded={expanded} onChange={this.handleExpand}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='subtitle1' component='h2'>
            {`${alreadyExisting ? 'Changer de' : 'Ajouter une'} carte bancaire`}
          </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.panel}>{form}</ExpansionPanelDetails>
      </ExpansionPanel>
    );
  }

  /* */
  renderStatus() {
    const { classes } = this.props;
    const { refillStatus, product } = this.state;

    return (
      <Grid item xs={12} sm={12} md={12}>
        <Typography className={classes.bold} variant='h5' component='h2'>
          {'Rechargement automatique'}
        </Typography>
        {(!refillStatus || (refillStatus && !refillStatus.status)) && (
          <React.Fragment>
            <div className={classes.textContainer}>
              <Typography paragraph variant='body1'>
                {
                  "Choisissez 2, 5, 10, 20 ou 30 seaux. Lorsque le solde de votre carte atteint 2 seaux, le rechargement s'effectuera automatiquement."
                }
              </Typography>
              <Typography variant='body1'>{'Rendez-vous plus serein à votre golf !'}</Typography>
            </div>
            <Button
              size='large'
              color='secondary'
              variant='contained'
              onClick={() => this.toggleModal('chooseProduct')}
              className={classes.largeButton}
            >
              {'Activer'}
            </Button>
            {this.renderChooseProductModal()}
            {this.renderSelectCreditCardModal()}
          </React.Fragment>
        )}
        {refillStatus && refillStatus.status && (
          <React.Fragment>
            <div className={classes.textContainer}>
              <Typography align='justify' paragraph variant='body1'>
                {'Le rechargement automatique est actuellement '}
                <b>{`${refillStatus.status ? 'activé' : 'désactivé'}`}</b>
                {'.'}
              </Typography>
              {product && (
                <Typography variant='body2'>
                  {'Produit sélectionné : '}
                  <b>{`${product.name} (${i18n.l('currency', product.price / 100)})`}</b>
                  {'.'}
                </Typography>
              )}
            </div>
            <Button
              size='large'
              variant='contained'
              onClick={() => this.toggleModal('optOut')}
              className={classes.largeButton}
            >
              {'Désactiver'}
            </Button>
            {this.renderOptOutModal()}
          </React.Fragment>
        )}
      </Grid>
    );
  }

  /* */
  selectProduct = (product) => () => this.setState({ product });

  /* */
  renderProduct = (p, k) => {
    const { classes } = this.props;
    const { product } = this.state;

    const className = classNames(classes.product, {
      [classes.selectedProduct]: product === p,
    });

    return (
      <Paper key={k} className={className} onClick={this.selectProduct(p)}>
        <Typography>{p.name.toUpperCase()}</Typography>
        <Typography variant='h6' color='textSecondary'>
          {i18n.l('currency', p.price / 100)}
        </Typography>
      </Paper>
    );
  };

  /* */
  renderProductPicker() {
    const { loading, products } = this.state;

    if (loading) {
      return (
        <Box display='flex' justifyContent='center'>
          <CircularProgress size={24} color='secondary' />
        </Box>
      );
    }

    if (products && products.length === 0) {
      return <Typography align='center'>{'Aucun produit disponible.'}</Typography>;
    }

    return (
      <Box mt={4} mb={4}>
        {products.map(this.renderProduct)}
      </Box>
    );
  }

  /* */
  renderImprint() {
    const { imprint } = this.state;
    const { classes } = this.props;

    return (
      <React.Fragment>
        {imprint && <CreditCardPreview brand={imprint.brand} dueDate={imprint.dueDate} number={imprint.number} />}
        {imprint && imprint.isExpired && (
          <Alert variant='error' className={classes.alert}>
            {'La carte est expirée.'}
          </Alert>
        )}
        {imprint && imprint.isSoonExpired && (
          <Alert variant='warning' className={classes.alert}>
            {'La carte arrive bientôt à expiration.'}
          </Alert>
        )}
      </React.Fragment>
    );
  }

  /* */
  renderChooseProductModal() {
    const { classes, fullScreen } = this.props;
    const { chooseProductModalOpened, product } = this.state;

    return (
      <Dialog
        fullWidth
        maxWidth={'md'}
        fullScreen={fullScreen}
        open={chooseProductModalOpened}
        classes={{ paperFullWidth: classes.dialog }}
        onClose={() => this.toggleModal('chooseProduct')}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant='h6' component='span'>
            {'Sélectionnez un produit'}
          </Typography>
          <IconButton
            color='primary'
            size='medium'
            onClick={() => this.toggleModal('chooseProduct')}
            className={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {this.renderProductPicker()}
          <Typography variant='body1' component='span'>
            {'Dès que votre solde atteindra '}
            <b>{'2'}</b>
            {' seaux, vous serez recrédité automatiquement de '}
            <b>{`${product && product.value}`}</b>
            {' seaux.'}
          </Typography>
          <Typography variant='body1' component='span'>
            {"Attention, le prix est susceptible d'évoluer. Vous serez averti par e-mail de tout changement."}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            size='large'
            color='secondary'
            variant='contained'
            onClick={() => this.toggleModal('selectCreditCard')}
            className={classes.dialogButton}
          >
            {'SUIVANT'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  /* */
  renderSelectCreditCardModal() {
    const { classes, fullScreen, golf } = this.props;
    const { selectCreditCardModalOpened, buttonLoading, imprint, paylineToken, paylineLoading } = this.state;
    const isPayline = PaymentProvider.isPayline(golf);

    return (
      <Dialog
        fullWidth
        maxWidth={'md'}
        fullScreen={fullScreen}
        open={selectCreditCardModalOpened}
        classes={{ paperFullWidth: classes.dialog }}
        onClose={() => this.toggleModal('selectCreditCard')}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant='h6' component='span'>
            {'Validez votre moyen de paiement'}
          </Typography>
          <IconButton
            color='primary'
            size='medium'
            onClick={() => this.toggleModal('selectCreditCard')}
            className={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container justify='center' spacing={16}>
            {imprint && (
              <Grid item xs={11} sm={8} md={6}>
                {this.renderImprint()}
                <br />
                <Typography variant='body2' component='span'>
                  {'Le rechargement automatique utilisera cette carte bancaire.'}
                </Typography>
              </Grid>
            )}
            {isPayline && (
              <Grid item xs={11} sm={8} md={6}>
                {paylineToken ? (
                  <PaylineWidgetFrame
                    token={paylineToken}
                    options={{
                      // Enrôlement de carte, pas un achat : le montant de la session
                      // (1 € d'autorisation, ou 0 € si le golf est en demande
                      // d'information) n'est pas un prix et n'a rien à faire à l'écran.
                      flowType: PAYLINE_FLOW_TYPES.SAVE_CARD,
                    }}
                    title='Payline Widget'
                  />
                ) : (
                  <Box display='flex' flexDirection='column' alignItems='center' py={4}>
                    <Typography variant='body1' paragraph align='center'>
                      {imprint
                        ? 'Vous pouvez changer de carte bancaire ci-dessous.'
                        : "Pour activer le rechargement automatique, vous devez d'abord enregistrer une carte bancaire."}
                    </Typography>
                    <Button
                      size='large'
                      color='secondary'
                      variant='contained'
                      onClick={this.handlePaylineCardRegistration}
                      disabled={paylineLoading}
                    >
                      {paylineLoading ? (
                        <CircularProgress size={24} color='secondary' />
                      ) : imprint ? (
                        'Changer de carte'
                      ) : (
                        'Enregistrer ma carte'
                      )}
                    </Button>
                  </Box>
                )}
              </Grid>
            )}
            {!isPayline && (
              <Grid item xs={11} sm={8} md={6}>
                <Alert variant='warning'>
                  {'Le paiement en ligne n\'est pas encore activé pour ce golf. Contactez Primagolf pour configurer Payline.'}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button size='large' onClick={() => this.toggleModal('selectCreditCard')} className={classes.dialogButton}>
            {'RETOUR'}
          </Button>
          <Button
            size='large'
            color='secondary'
            variant='contained'
            onClick={this.enableRefill}
            disabled={buttonLoading || !imprint || (imprint && imprint.isExpired)}
            className={classes.dialogButton}
          >
            {buttonLoading ? <CircularProgress size={28} thickness={4} color='secondary' /> : 'ACTIVER'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  /* */
  renderOptOutModal() {
    const { buttonLoading, optOutModalOpened } = this.state;
    const { classes, fullScreen } = this.props;

    return (
      <Dialog
        fullScreen={fullScreen}
        fullWidth
        maxWidth={'md'}
        classes={{ paperFullWidth: classes.dialog }}
        open={optOutModalOpened}
        onClose={() => this.toggleModal('optOut')}
      >
        <DialogTitle className={classes.dialogTitle}>
          <IconButton
            color='primary'
            size='medium'
            onClick={() => this.toggleModal('optOut')}
            className={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant='h6' component='span'>
            {'Êtes-vous sûr ?'}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant='body1' component='span'>
            {
              'En désactivant le rechargement automatique, vous ne serez plus crédité automatiquement lorsque votre solde sera faible.'
            }
          </Typography>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            fullWidth
            size='large'
            variant='contained'
            onClick={this.disableRefill}
            disabled={buttonLoading}
            className={classes.dialogButton}
          >
            {buttonLoading ? <CircularProgress size={28} thickness={4} color='secondary' /> : 'DÉSACTIVER'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  /* */
  render() {
    const { loading, error, success } = this.state;

    return (
      <Screen
        error={error}
        loading={loading}
        success={success}
        title='Practice'
        helpURL={`${HELP_URL}/carte-bancaire`}
        onBackPress={() => this.props.history.goBack()}
      >
        <Grid container spacing={40} justify='center'>
          {this.renderStatus()}
        </Grid>
      </Screen>
    );
  }
}

const mapStateToProps = ({ app }) => ({ account: app.account, golf: app.golf });

const StyledComponent = withStyles(styles)(AutoRefill);
const WithMobileDialogComponent = withMobileDialog({ breakpoint: 'xs' })(StyledComponent);

export default connect(mapStateToProps, CreditCard.actions)(WithMobileDialogComponent);
